import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "./Footer";
import { CONTACT } from "../../lib/data";
import logoWhite from "../../assets/logo-white-transparent.png";

const LAST_UPDATED = "August 2026";

const LegalLayout = ({ title, kicker, children, testid }) => {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return (
    <div style={{ background: "var(--bg)" }} data-testid={testid}>
      <header style={{ position: "sticky", top: 0, zIndex: 1000, background: "rgba(14,14,14,0.9)",
        backdropFilter: "blur(14px)", padding: "14px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, color: "#fff",
            fontFamily: "'Bebas Neue'", fontSize: 24, letterSpacing: "0.08em" }}>
            <img src={logoWhite} alt="VLA" style={{ width: 40, height: 40, objectFit: "contain" }} /> VLA
          </Link>
          <Link to="/" className="btn btn-outline" style={{ padding: "10px 20px", fontSize: 12 }}>
            <ArrowLeft size={15} /> Back to site
          </Link>
        </div>
      </header>

      <section style={{ padding: "80px 0 40px", background: "var(--bg-alt)" }}>
        <div className="container" style={{ maxWidth: 860 }}>
          <p className="eyebrow">{kicker}</p>
          <h1 className="section-title" style={{ marginTop: 14 }}>{title}</h1>
          <p style={{ marginTop: 16, color: "var(--muted)", fontSize: 14 }}>Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <section style={{ padding: "60px 0 100px" }}>
        <div className="container" style={{ maxWidth: 860 }}>
          <article className="legal-body" style={{ fontSize: 15.5, color: "var(--text)", lineHeight: 1.75 }}>
            {children}
          </article>
        </div>
      </section>

      <Footer onNav={() => { window.location.href = "/"; }} />
    </div>
  );
};

const H2 = ({ children }) => (
  <h2 style={{ fontFamily: "'Inter'", fontSize: 21, fontWeight: 800, letterSpacing: "0.01em",
    color: "var(--black)", marginTop: 34, marginBottom: 12 }}>{children}</h2>
);
const P = ({ children }) => <p style={{ marginBottom: 14 }}>{children}</p>;
const UL = ({ children }) => (
  <ul style={{ listStyle: "disc", paddingLeft: 22, margin: "0 0 14px" }}>{children}</ul>
);
const LI = ({ children }) => <li style={{ marginBottom: 6 }}>{children}</li>;

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" kicker="Your Privacy" testid="privacy-page">
    <P>
      Victorious Lions Academy ("VLA", "we", "our", "us") respects your privacy. This Privacy Policy
      explains what information we collect through our website, why we collect it, how we use it,
      and the choices you have.
    </P>

    <H2>1. Information We Collect</H2>
    <P>When you submit an enquiry or free-trial request through our website, we collect the information you provide, which typically includes:</P>
    <UL>
      <LI>Full name</LI>
      <LI>Phone number</LI>
      <LI>Email address</LI>
      <LI>The program you are interested in (optional)</LI>
      <LI>Any message or enquiry details you choose to share</LI>
    </UL>
    <P>Our servers may also automatically receive basic technical information such as your IP address and browser type, used only for security, rate-limiting, and preventing abuse.</P>

    <H2>2. Why We Collect It</H2>
    <P>We collect this information for the following limited purposes:</P>
    <UL>
      <LI>To respond to your enquiry and help you book a free trial class</LI>
      <LI>To keep a record of your interest so our coaching team can follow up</LI>
      <LI>To send you an acknowledgement email confirming that we have received your enquiry</LI>
      <LI>To improve our website and communication</LI>
    </UL>

    <H2>3. How We Use Your Information</H2>
    <P>Your information is used only by the Victorious Lions Academy team to contact you about the enquiry you submitted or the program you asked about. We do not send unsolicited marketing messages.</P>

    <H2>4. How Your Information Is Stored & Protected</H2>
    <P>Enquiries are stored in a private database accessible only to authorised VLA administrators. Access is protected using industry-standard authentication. We take reasonable technical and organisational measures to protect the information from unauthorised access, disclosure, alteration, or destruction.</P>

    <H2>5. Third Parties</H2>
    <P>We do not sell or rent your personal information. To operate the website and respond to enquiries, we rely on a small number of trusted service providers, including:</P>
    <UL>
      <LI>Our website hosting provider</LI>
      <LI>Our transactional email provider (used only to send enquiry confirmations and internal notifications)</LI>
    </UL>
    <P>These providers process information only on our instructions and only to the extent needed to provide their service.</P>

    <H2>6. Your Rights</H2>
    <P>You may at any time contact us to:</P>
    <UL>
      <LI>Ask what information we hold about you</LI>
      <LI>Request correction of inaccurate information</LI>
      <LI>Request deletion of your information, subject to any legitimate reason we may have to retain it</LI>
    </UL>

    <H2>7. Cookies</H2>
    <P>Our website uses only the technical storage necessary for it to function correctly. We do not use tracking or advertising cookies as part of this website.</P>

    <H2>8. Children's Privacy</H2>
    <P>Because we run programs for children, enquiries about kids' classes are usually submitted by a parent or guardian. We do not knowingly collect information directly from children through this website.</P>

    <H2>9. Changes to This Policy</H2>
    <P>We may update this Privacy Policy from time to time. The most current version will always be available on this page with an updated "Last updated" date.</P>

    <H2>10. Contact Us</H2>
    <P>If you have any questions about this Privacy Policy or your information, please contact us:</P>
    <UL>
      <LI>Email: <a href={`mailto:${CONTACT.email}`} style={{ color: "var(--red)" }}>{CONTACT.email}</a></LI>
      <LI>Phone: <a href={`tel:+${CONTACT.phoneRaw}`} style={{ color: "var(--red)" }}>{CONTACT.phone}</a></LI>
      <LI>Address: {CONTACT.addressLine}</LI>
    </UL>
  </LegalLayout>
);

export const TermsConditions = () => (
  <LegalLayout title="Terms & Conditions" kicker="Website Terms" testid="terms-page">
    <P>Welcome to the Victorious Lions Academy website. By accessing or using this website, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use the website.</P>

    <H2>1. Website Usage</H2>
    <P>You agree to use this website only for lawful purposes and in a manner that does not infringe the rights of, restrict, or inhibit anyone else's use of the website. You must not attempt to gain unauthorised access to any part of the website, its servers, or its underlying systems.</P>

    <H2>2. Enquiry & Submission Terms</H2>
    <P>When you submit information through the enquiry / free-trial form, you agree that:</P>
    <UL>
      <LI>The information you provide is accurate and up to date</LI>
      <LI>You are submitting your own information, or you have the authority of the person whose information is being submitted (for example, a parent or guardian submitting on behalf of a child)</LI>
      <LI>We may use the information to contact you about your enquiry as described in our <a href="/privacy-policy" style={{ color: "var(--red)" }}>Privacy Policy</a></LI>
    </UL>
    <P>To help protect the form from abuse, we may apply reasonable technical measures such as rate limits and validation. Submissions that appear automated or malformed may be rejected.</P>

    <H2>3. Academy & Program Information</H2>
    <P>Information about our programs, schedules, fees, and coaching on this website is provided for general information only. While we make every effort to keep this information accurate and current, it may change from time to time. Final details will always be confirmed with you by the VLA team.</P>

    <H2>4. Free Trial Classes</H2>
    <P>Free trial classes are offered subject to availability and coach scheduling. Booking a trial through the website is a request, not a guaranteed confirmed slot; the VLA team will contact you to confirm timing.</P>

    <H2>5. Intellectual Property</H2>
    <P>All content on this website — including text, photographs, graphics, the Victorious Lions Academy name and lion crest, and page design — belongs to Victorious Lions Academy or is used with permission. You may not copy, republish, or use this content for commercial purposes without our prior written consent.</P>

    <H2>6. Third-Party Links</H2>
    <P>Our website may link to third-party services such as Google Maps, WhatsApp, or Instagram. We are not responsible for the content, policies, or practices of those third-party services.</P>

    <H2>7. Website Availability & Limitations</H2>
    <P>We make reasonable efforts to keep the website available and functional, but we do not guarantee that it will always be uninterrupted or free from errors. To the fullest extent permitted by applicable law, Victorious Lions Academy will not be liable for any indirect, incidental, or consequential loss arising from your use of this website.</P>

    <H2>8. Changes to These Terms</H2>
    <P>We may update these Terms & Conditions from time to time. The most current version will always be available on this page with an updated "Last updated" date. Your continued use of the website after changes means you accept the updated terms.</P>

    <H2>9. Contact Us</H2>
    <P>If you have any questions about these Terms & Conditions, please contact us:</P>
    <UL>
      <LI>Email: <a href={`mailto:${CONTACT.email}`} style={{ color: "var(--red)" }}>{CONTACT.email}</a></LI>
      <LI>Phone: <a href={`tel:+${CONTACT.phoneRaw}`} style={{ color: "var(--red)" }}>{CONTACT.phone}</a></LI>
      <LI>Address: {CONTACT.addressLine}</LI>
    </UL>
  </LegalLayout>
);
